import { useEffect, useMemo, useState } from "react";
import { useStores } from "../../stores/root-store";
import { observer } from "mobx-react";
import _ from "lodash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEdit,
  faPrint,
  faShareAlt,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import {
  currentDateString,
  formatDate,
  getDiscountDetail,
  ShowAlert,
  showConfirmationDialog,
} from "../../utils/functions";
import { IVoucher } from "../../stores/voucher/index.d";
import moment from "moment";
import CopyButton from "../../shared/copy-button";
import ShareVoucherForm from "./share-voucher-form";
import PrintedVoucher from "./printed-voucher";
import PreviewVoucherEmail from "./preview-voucher-email";

const cols: string[] = [
  "#",
  "Voucher Code",
  "Voucher Duration",
  "Discount",
  "Min. Order Value",
  "Usage Limitations",
  "Status",
  "Actions",
];

const VoucherListing = observer(() => {
  const { voucherStore } = useStores();
  const {
    deleteVoucher,
    vouchers,
    fetchVouchers,
    setEditVoucher,
    previewEmailBody,
    setPreviewEmailBody,
  } = voucherStore;

  const [sharableVoucher, setSharableVoucher] = useState<IVoucher | null>(null);
  const [printedVoucher, setPrintedVoucher] = useState<IVoucher | null>(null);

  useEffect(() => {
    fetchVouchers();
  }, []);

  const voucherList = useMemo(() => {
    return vouchers;
  }, [vouchers]);

  const handleDelete = async (n: number) => {
    showConfirmationDialog(() => {
      deleteVoucher(n, (res: boolean) => {
        if (res) {
          ShowAlert.Success("The item has been deleted.");
          fetchVouchers();
        } else {
          ShowAlert.Error("Request failed. Please try again.");
        }
      });
    });
  };

  return (
    <>
      <table className="table table-responsive table-striped shadow-lg">
        <thead>
          <tr>
            {cols.map((c, i) => (
              <th
                scope="col"
                className="border-top-0 border-start-0 border-end-0 border-3 border-warning"
                key={i}
              >
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {voucherList.map((voucher: IVoucher, i: number) => {
            const isActive = moment(voucher.endDate).isSameOrAfter(
              currentDateString()
            );

            return (
              <tr key={voucher.voucherId}>
                <th scope="row">{i + 1}</th>
                <td>
                  {voucher.voucherCode}{" "}
                  <CopyButton textToCopy={voucher.voucherCode} />
                </td>
                <td>{`${formatDate(voucher.startDate, "DD MMM")} - ${formatDate(
                  voucher.endDate,
                  "DD MMM"
                )}`}</td>
                <td>{getDiscountDetail(voucher)}</td>
                <td>£{voucher.minOrderValue}</td>
                <td className="small">
                  Total:{" "}
                  <b className="small">{voucher.maxUsesTotal || "Unlimited"}</b>{" "}
                  | Per User:
                  <b className="small">
                    {voucher.maxUsesPerUser || "Unlimited"}
                  </b>
                </td>
                <td className={isActive ? "text-success" : "text-danger"}>
                  {isActive ? "Active" : "Expired"}
                </td>
                <td>
                  <div className="btn-group">
                    <button
                      className="btn"
                      onClick={() => setSharableVoucher(voucher)}
                      title="Share voucher"
                    >
                      <FontAwesomeIcon
                        icon={faShareAlt}
                        className="text-success"
                      />
                    </button>
                    <button
                      className="btn border-start-0 border-top-0 border-bottom-0 border-secondary"
                      onClick={() => setPrintedVoucher(voucher)}
                      title="Printed Voucher"
                    >
                      <FontAwesomeIcon icon={faPrint} className="text-info" />
                    </button>
                    <button
                      className="btn"
                      onClick={() => setEditVoucher(voucher)}
                    >
                      <FontAwesomeIcon icon={faEdit} className="text-primary" />
                    </button>
                    <button
                      className="btn"
                      onClick={() => handleDelete(voucher.voucherId)}
                    >
                      <FontAwesomeIcon icon={faTrash} className="text-danger" />
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
          {!voucherList?.length && (
            <tr>
              <th colSpan={8} className="text-center">
                No data available
              </th>
            </tr>
          )}
        </tbody>
      </table>
      {sharableVoucher && (
        <ShareVoucherForm
          show={sharableVoucher != null}
          setShowModal={(show: boolean) => {
            !show && setSharableVoucher(null);
          }}
          voucher={sharableVoucher}
        />
      )}
      {printedVoucher && (
        <PrintedVoucher
          show={printedVoucher != null}
          setShowModal={(show: boolean) => {
            !show && setPrintedVoucher(null);
          }}
          voucher={printedVoucher}
        />
      )}
      {previewEmailBody && (
        <PreviewVoucherEmail
          show={previewEmailBody != null}
          setShowModal={(show: boolean) => {
            !show && setPreviewEmailBody(null);
          }}
          emailBody={previewEmailBody}
        />
      )}
    </>
  );
});

export default VoucherListing;
